/**
 * ActivityFilterToolbar (Story 34.5 — restyled to match ClientFilterToolbar).
 *
 * Same visual language as the clients toolbar: a max-w-xs search box on the left,
 * a "Filters" dropdown (Resource / Action multi-select) with the tabbed popover,
 * the date-range segmented control pushed to the right, and an active-filter chip
 * row underneath. Fully controlled — the parent owns filter state (synced to the
 * URL) and this only emits intent.
 */

import { useState } from "react";
import { Input, Button, cn } from "@provenleap/ui";
import {
  MagnifyingGlass,
  X,
  Funnel,
  Check,
  CaretDown,
  Tag,
  Lightning,
  CalendarBlank,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";
import { ACTION_OPTIONS, RESOURCE_TYPE_OPTIONS } from "./activityVocab";

export type DateRangePreset = "24h" | "7d" | "30d" | "custom" | null;

interface ActivityFilterToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  selectedResourceTypes: string[];
  onToggleResourceType: (rt: string) => void;
  selectedActions: string[];
  onToggleAction: (action: string) => void;
  datePreset: DateRangePreset;
  onDatePresetChange: (preset: DateRangePreset) => void;
  /** ISO yyyy-mm-dd for the custom range (inclusive). */
  customStart?: string;
  customEnd?: string;
  onCustomRangeChange: (start: string | undefined, end: string | undefined) => void;
  onClearFilters: () => void;
}

type FilterTab = "resource" | "action";

const DATE_PRESETS: Array<{ value: Exclude<DateRangePreset, null | "custom">; label: string }> = [
  { value: "24h", label: "24h" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
];

export function ActivityFilterToolbar({
  search,
  onSearchChange,
  selectedResourceTypes,
  onToggleResourceType,
  selectedActions,
  onToggleAction,
  datePreset,
  onDatePresetChange,
  customStart,
  customEnd,
  onCustomRangeChange,
  onClearFilters,
}: ActivityFilterToolbarProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<FilterTab>("resource");
  const [filterSearch, setFilterSearch] = useState("");

  const totalFilterCount = selectedResourceTypes.length + selectedActions.length;
  const hasAnyFilter = totalFilterCount > 0 || !!search || datePreset != null;

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: "resource", label: "Resource", count: selectedResourceTypes.length },
    { key: "action", label: "Action", count: selectedActions.length },
  ];

  const filteredResources = RESOURCE_TYPE_OPTIONS.filter(
    (r) => !filterSearch || r.label.toLowerCase().includes(filterSearch.toLowerCase())
  );
  const filteredActions = ACTION_OPTIONS.filter(
    (a) =>
      !filterSearch ||
      a.label.toLowerCase().includes(filterSearch.toLowerCase()) ||
      a.value.toLowerCase().includes(filterSearch.toLowerCase())
  );

  return (
    <div className="mb-4 space-y-2">
      {/* Row 1: Search + Filters + Date range */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search input */}
        <div className="relative max-w-xs flex-1">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground"
          />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by actor email..."
            aria-label="Search by actor email"
            className="pl-9 pr-9 rounded-2xl text-foreground placeholder:text-foreground shadow-xs text-[13.5px]"
            style={{ border: "0.5px solid var(--hairline)" }}
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 min-h-11 min-w-11 flex items-center justify-center text-foreground/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Filters dropdown */}
        <div className="relative">
          <Button
            variant="outline"
            className="shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={() => {
              setFilterOpen(!filterOpen);
              setFilterSearch("");
            }}
          >
            <Funnel size={16} className="mr-1" />
            Filters
            {totalFilterCount > 0 && (
              <span className="ml-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {totalFilterCount}
              </span>
            )}
            <CaretDown size={12} className="ml-1" />
          </Button>

          <AnimatePresence>
            {filterOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => {
                    setFilterOpen(false);
                    setFilterSearch("");
                  }}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -4 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="absolute left-0 top-full mt-2.5 z-50 w-72 bg-popover rounded-2xl shadow-md p-2"
                  style={{ border: "0.5px solid var(--hairline)" }}
                >
                  <div
                    className="absolute -top-[5px] left-6 w-2.5 h-2.5 bg-popover rotate-45 z-10"
                    style={{
                      borderLeft: "0.5px solid var(--hairline)",
                      borderTop: "0.5px solid var(--hairline)",
                    }}
                  />
                  {/* Tab bar */}
                  <div className="flex mx-4 pb-0">
                    {tabs.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => {
                          setActiveTab(tab.key);
                          setFilterSearch("");
                        }}
                        className={cn(
                          "flex-1 px-3 py-2.5 text-[13px] font-medium transition-colors cursor-pointer",
                          activeTab === tab.key
                            ? "text-foreground"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        {tab.label}
                        {tab.count > 0 && (
                          <span className="ml-1 inline-flex items-center justify-center bg-primary text-white text-[10px] rounded-full w-4 h-4">
                            {tab.count}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Search */}
                  <div className="px-2 pt-2 pb-0.5">
                    <div className="relative">
                      <MagnifyingGlass
                        size={16}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                      />
                      <input
                        type="text"
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                        placeholder={activeTab === "resource" ? "Search resources..." : "Search actions..."}
                        aria-label="Search filter options"
                        className="w-full pl-8 pr-3 py-2 text-[13px] text-foreground bg-transparent placeholder:text-muted-foreground placeholder:font-normal font-medium rounded-lg border-[0.5px] border-input focus:outline-none focus-visible:outline-none"
                        style={{ outline: "none", boxShadow: "none" }}
                      />
                    </div>
                  </div>

                  {/* Tab content */}
                  <div className="max-h-[260px] overflow-y-auto pt-2 pb-1">
                    {activeTab === "resource" &&
                      filteredResources.map((opt) => {
                        const isSelected = selectedResourceTypes.includes(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => onToggleResourceType(opt.value)}
                            className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                          >
                            <span
                              className={cn(
                                "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                isSelected && "bg-primary border-primary text-white"
                              )}
                            >
                              {isSelected && <Check size={12} />}
                            </span>
                            {opt.label}
                          </button>
                        );
                      })}

                    {activeTab === "action" &&
                      filteredActions.map((opt) => {
                        const isSelected = selectedActions.includes(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => onToggleAction(opt.value)}
                            className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                          >
                            <span
                              className={cn(
                                "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                isSelected && "bg-primary border-primary text-white"
                              )}
                            >
                              {isSelected && <Check size={12} />}
                            </span>
                            {opt.label}
                          </button>
                        );
                      })}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* Date-range segmented control — pushed to the right */}
        <div
          className="flex items-center gap-1 rounded-2xl p-1 shadow-xs bg-background sm:ml-auto"
          style={{ border: "0.5px solid var(--hairline)" }}
          role="group"
          aria-label="Date range"
        >
          <CalendarBlank size={16} className="ml-1.5 text-muted-foreground" />
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => onDatePresetChange(datePreset === preset.value ? null : preset.value)}
              aria-pressed={datePreset === preset.value}
              className={cn(
                "rounded-xl px-2.5 py-1.5 text-[12.5px] font-medium transition-colors cursor-pointer",
                datePreset === preset.value
                  ? "bg-primary text-white"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {preset.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => onDatePresetChange(datePreset === "custom" ? null : "custom")}
            aria-pressed={datePreset === "custom"}
            className={cn(
              "rounded-xl px-2.5 py-1.5 text-[12.5px] font-medium transition-colors cursor-pointer",
              datePreset === "custom" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"
            )}
          >
            Custom
          </button>
        </div>
      </div>

      {/* Custom date range inputs */}
      {datePreset === "custom" && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[12.5px] text-muted-foreground">From</label>
          <input
            type="date"
            value={customStart ?? ""}
            onChange={(e) => onCustomRangeChange(e.target.value || undefined, customEnd)}
            aria-label="Custom range start"
            className="rounded-xl px-2.5 py-1.5 text-[13px] text-foreground bg-background border-[0.5px] border-input shadow-xs focus:outline-none"
          />
          <label className="text-[12.5px] text-muted-foreground">To</label>
          <input
            type="date"
            value={customEnd ?? ""}
            onChange={(e) => onCustomRangeChange(customStart, e.target.value || undefined)}
            aria-label="Custom range end"
            className="rounded-xl px-2.5 py-1.5 text-[13px] text-foreground bg-background border-[0.5px] border-input shadow-xs focus:outline-none"
          />
        </div>
      )}

      {/* Row 2: active filter chips */}
      {hasAnyFilter && (
        <div className="flex flex-wrap items-center gap-1">
          {selectedResourceTypes.map((rt) => {
            const opt = RESOURCE_TYPE_OPTIONS.find((o) => o.value === rt);
            return (
              <FilterChip
                key={`rt-${rt}`}
                icon={<Tag size={14} className="shrink-0 text-foreground mr-1.5" />}
                label={opt?.label ?? rt}
                onRemove={() => onToggleResourceType(rt)}
                removeLabel={`Remove resource filter ${opt?.label ?? rt}`}
              />
            );
          })}
          {selectedActions.map((a) => {
            const opt = ACTION_OPTIONS.find((o) => o.value === a);
            return (
              <FilterChip
                key={`a-${a}`}
                icon={<Lightning size={14} className="shrink-0 text-foreground mr-1.5" />}
                label={opt?.label ?? a}
                onRemove={() => onToggleAction(a)}
                removeLabel={`Remove action filter ${opt?.label ?? a}`}
              />
            );
          })}
          {datePreset && (
            <FilterChip
              icon={<CalendarBlank size={14} className="shrink-0 text-foreground mr-1.5" />}
              label={datePreset === "custom" ? "Custom range" : `Last ${datePreset}`}
              onRemove={() => onDatePresetChange(null)}
              removeLabel="Remove date filter"
            />
          )}
          <button
            type="button"
            onClick={onClearFilters}
            className="text-[13px] hover:text-foreground min-h-[44px] px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
            style={{ color: "#5A7396" }}
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}

function FilterChip({
  icon,
  label,
  onRemove,
  removeLabel,
}: {
  icon: React.ReactNode;
  label: string;
  onRemove: () => void;
  removeLabel: string;
}) {
  return (
    <span className="inline-flex items-center w-fit rounded-2xl bg-background border-[0.5px] border-input px-4 text-[13px] h-10 font-medium text-foreground whitespace-nowrap">
      {icon}
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="ml-2.5 text-foreground hover:text-destructive transition-colors leading-none flex items-center shrink-0 cursor-pointer"
        style={{ minWidth: 0, minHeight: 0 }}
        aria-label={removeLabel}
      >
        <X size={14} />
      </button>
    </span>
  );
}
